import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
export interface SelectOption {
  id: string;
  label: string;
  hint?: string;
  group?: string;
}
interface SelectMenuProps {
  ariaLabel: string;
  options: SelectOption[];
  value: string;
  onChange: (id: string) => void;
  icon?: React.ReactNode;
  triggerClassName?: string;
  menuClassName?: string;
}
export function SelectMenu({
  ariaLabel,
  options,
  value,
  onChange,
  icon,
  triggerClassName = 'w-52',
  menuClassName = 'w-64'
}: SelectMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = options.find(option => option.id === value) ?? options[0];
  const groups = Array.from(new Set(options.map(option => option.group ?? '')));
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false);
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);
  return <div ref={containerRef} className="relative" data-id="element-146" data-mp="25dpk1ekpdkar">
      <button type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} onClick={() => setOpen(state => !state)} className={`inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 ${triggerClassName}`} data-id="element-147" data-mp="21egqa9pnxmf7">
        {icon}
        <span className="flex-1 truncate text-left" data-id="element-148" data-mp="g1470nz5n7u4">{selected.label}</span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-149" data-mp="g2xda25uvq1l" />
      </button>

      {open ? <ul role="listbox" aria-label={ariaLabel} className={`absolute right-0 z-30 mt-2 max-h-96 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg ${menuClassName}`} data-id="element-150" data-mp="2p3ienu1heue1">
          {groups.map(group => <React.Fragment key={group || 'default'} data-id="element-151" data-mp="2z1g24fbion5l">
              {group ? <li aria-hidden="true" className="px-3.5 pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-400" data-id="element-152" data-mp="33zhuxryqw0u9">
                  {group}
                </li> : null}
              {options.filter(option => (option.group ?? '') === group).map(option => {
          const isSelected = option.id === selected.id;
          return <li key={option.id} role="option" aria-selected={isSelected} data-id="element-153" data-mp="q3afjcn834h">
                      <button type="button" onClick={() => {
              onChange(option.id);
              setOpen(false);
            }} className={`flex w-full items-center justify-between gap-3 px-3.5 py-2 text-left text-sm transition hover:bg-slate-50 ${isSelected ? 'font-semibold text-brand-800' : 'text-slate-700'}`} data-id="element-154" data-mp="3rwe8r7q9qps4">
                        <span data-id="element-155" data-mp="9pq2vfne9yle">
                          {option.label}
                          {option.hint ? <span className="ml-2 text-xs font-medium text-slate-400" data-id="element-156" data-mp="5o4ti4a2k2fy">{option.hint}</span> : null}
                        </span>
                        {isSelected ? <CheckIcon className="h-4 w-4 shrink-0 text-brand-700" aria-hidden="true" data-id="element-157" data-mp="xej3hpeyoumc" /> : null}
                      </button>
                    </li>;
        })}
            </React.Fragment>)}
        </ul> : null}
    </div>;
}